import { useTranslation } from 'react-i18next'
import IconButton from '@/shared/components/IconButton/IconButton'
import styles from './RowToolbar.module.scss'

interface RowToolbarProps {
  /**
   * Counter-scale. The toolbar lives inside the zoomed canvas, so at a fit of
   * 0.55 it was drawing at 55% of the app's icon size. This puts it back to
   * full size without taking it out of the row it belongs to.
   */
  scale: number
  /** Keyboard route to selecting the row; the mouse can click the row itself. */
  onSelect: () => void
  isHidden: boolean
  isFirst: boolean
  isLast: boolean
  onMoveUp: () => void
  onMoveDown: () => void
  onDuplicate: () => void
  onToggleHidden: () => void
  onDelete: () => void
}

/**
 * The five things a row can have done to it, in the order the brief lists.
 *
 * Drawn at the app's medium glyph, not its big one: this is chrome over
 * somebody's page, and at 24px it was the loudest thing on the canvas.
 */
function RowToolbar(props: RowToolbarProps) {
  const {
    scale,
    onSelect,
    isHidden,
    isFirst,
    isLast,
    onMoveUp,
    onMoveDown,
    onDuplicate,
    onToggleHidden,
    onDelete
  } = props
  const { t } = useTranslation()

  return (
    // The plain class is the hook the canvas uses to reveal this on hover; the
    // module class carries the looks.
    <div className={`mp-row-toolbar ${styles.toolbar}`} style={{ zoom: scale }}>
      <IconButton
        icon="las la-mouse-pointer icon-medium"
        color="primary"
        onClick={onSelect}
        ariaLabel={t('WEBSITE.ROW.SELECT')}
      />
      {!isFirst && (
        <IconButton
          icon="las la-arrow-up icon-medium"
          color="primary"
          onClick={onMoveUp}
          ariaLabel={t('WEBSITE.ROW.MOVE_UP')}
        />
      )}
      {!isLast && (
        <IconButton
          icon="las la-arrow-down icon-medium"
          color="primary"
          onClick={onMoveDown}
          ariaLabel={t('WEBSITE.ROW.MOVE_DOWN')}
        />
      )}
      <IconButton
        icon="las la-copy icon-medium"
        color="primary"
        onClick={onDuplicate}
        ariaLabel={t('WEBSITE.ROW.DUPLICATE')}
      />
      <IconButton
        icon={isHidden ? 'las la-eye-slash icon-medium' : 'las la-eye icon-medium'}
        color="primary"
        onClick={onToggleHidden}
        ariaLabel={t(isHidden ? 'WEBSITE.ROW.SHOW' : 'WEBSITE.ROW.HIDE')}
      />
      <IconButton
        icon="las la-trash icon-medium"
        color="primary"
        onClick={onDelete}
        ariaLabel={t('WEBSITE.ROW.DELETE')}
      />
    </div>
  )
}

export default RowToolbar
